import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { NextIcon, PrevIcon } from "./icons";

export const FacetTypeEnum = {
  CATEGORY: "category",
} as const;
export type FacetTypeEnum = (typeof FacetTypeEnum)[keyof typeof FacetTypeEnum];

export interface FacetItemType {
  count: number;
  id: string;
  name: string;
  parentId?: string;
  slug: string;
}

export interface FacetType {
  items: FacetItemType[];
  type: string;
}

interface PaginationBaseType {
  count?: number;
  page?: number;
  pages?: number;
}

export interface PaginationType<ObjectType> extends PaginationBaseType {
  objects: ObjectType[];
}

export interface PaginationDataType<DataType> {
  count: number;
  facets?: FacetType[];
  hasNext?: boolean;
  hasPrev?: boolean;
  objects: DataType[];
  page: number;
  pages: number;
}

export interface PaginationProps extends PaginationBaseType {
  className?: string;
  path: string[];
  pathPrefix?: string;
  perPage?: number;
}

interface PageLinksProps {
  buttons: number[];
  hasMoreNextPages: boolean;
  hasMorePrevPages: boolean;
  page: number;
  pages: number;
  path: string[];
  pathPrefix: string;
}

const BUTTON_CLASSNAME =
  "border border-white/10 rounded-full size-7.5 transition-colors hover:bg-cosmic-cobalt";

function PageLinks({
  buttons,
  hasMoreNextPages,
  hasMorePrevPages,
  page,
  pages,
  path,
  pathPrefix,
}: PageLinksProps) {
  const navigate = useNavigate();

  const getPageUrl = (p: number) => {
    const u = [...path];
    u.push(pathPrefix + p);
    return `/${u.join("/")}`;
  };

  const handleClick = (nextPage: number) => () =>
    navigate({ to: getPageUrl(nextPage) });

  return (
    <ul className="flex items-center gap-1">
      {page > 1 && (
        <li>
          <Button
            aria-label="Previous page"
            className={BUTTON_CLASSNAME}
            onClick={handleClick(page - 1)}
          >
            <PrevIcon />
          </Button>
        </li>
      )}
      {hasMorePrevPages && <li>...</li>}
      {buttons.map((p) => (
        <li key={p}>
          <Link
            className={cn(
              "flex size-7.5 items-center justify-center rounded-full font-medium text-14 transition-colors hover:bg-cosmic-cobalt hover:text-white",
              page === p ? "bg-white text-space-gray-dark" : "text-white"
            )}
            to={getPageUrl(p)}
          >
            {p}
          </Link>
        </li>
      ))}
      {hasMoreNextPages && <li>...</li>}
      {page < pages && (
        <li>
          <Button
            aria-label="Next page"
            className={BUTTON_CLASSNAME}
            onClick={handleClick(page + 1)}
          >
            <NextIcon />
          </Button>
        </li>
      )}
    </ul>
  );
}

export default function Pagination({
  className,
  count = 0,
  page = 1,
  pages = 0,
  path = [],
  pathPrefix = "",
  perPage,
}: PaginationProps) {
  const pageLinksProps = useMemo(() => {
    let prevPage = 0;
    let nextPage = 0;

    const pageConfig: PageLinksProps = {
      buttons: [] as number[],
      hasMoreNextPages: false,
      hasMorePrevPages: false,
      page,
      pages,
      path,
      pathPrefix,
    };

    pageConfig.buttons.push(page);

    const addMorePages = (step: number) => {
      prevPage = page - step;
      nextPage = page + step;
      if (prevPage > 0) {
        pageConfig.buttons = [prevPage, ...pageConfig.buttons];
      }
      if (nextPage <= pages) {
        pageConfig.buttons.push(nextPage);
      }
    };

    addMorePages(1);

    if (pageConfig.buttons.length < 3) {
      addMorePages(2);
    }

    if (prevPage > 1) {
      pageConfig.hasMorePrevPages = true;
    }
    if (nextPage < pages) {
      pageConfig.hasMoreNextPages = true;
    }

    return pageConfig;
  }, [page, pages, path, pathPrefix]);

  const resultsCopy = useMemo(() => {
    if (!count) {
      return null;
    }
    if (!perPage) {
      return null;
    }

    const first = (page - 1) * perPage + 1;
    const last = Math.min(page * perPage, count);

    let copy = `Showing results ${first}`;

    if (first !== last) {
      copy += `-${last}`;
    }

    copy += ` of ${count}`;

    return copy;
  }, [page, perPage, count]);

  if (pages < 1) {
    return null;
  }

  return (
    <div className={cn("flex flex-col items-center gap-5", className)}>
      {pages > 1 && <PageLinks {...pageLinksProps} />}
      {resultsCopy && (
        <p className="text-13/[1.2em] text-white/80">{resultsCopy}</p>
      )}
    </div>
  );
}
